import React, { useMemo } from 'react';
import { useLazyLoadQuery } from 'react-relay';

import type { GlobalSideNavigationAGGRelayConnectionProps } from '@atlassian/global-side-navigation/common/types';
import GlobalSideNavigationAGGRelayConnectionQueryNode, {
	type GlobalSideNavigationAGGRelayConnectionQuery,
} from '@atlassian/global-side-navigation/GlobalSideNavigationAGGRelayConnectionQuery.graphql';
import {
	buildNavigationAriForMenuData,
	buildNavigationAriForSidebarData,
} from '@atlassian/navigation-common/navigation-ari';

// Use Confluence's ambient Relay environment so navigation reads and space mutations
// share a store. Reuse the exported operation to match the SSR preloader exactly.
export function GlobalSideNavigationAGGRelayConnection(
	props: GlobalSideNavigationAGGRelayConnectionProps,
): React.JSX.Element {
	const { children, activationId, entityAri, firstPartyApp, ownerAri, siteId } = props;
	const queryVariables = useMemo(
		() => ({
			cloudId: siteId || '',
			entityAriUserSideNavSettings: entityAri || '',
			fetchGlobalShortcutsAvailableProducts: Boolean(siteId && ownerAri),
			fetchOldEntityRecord: !!entityAri, // is false when entityAri is undefined, null, 0 or '' (false-y)
			navPlatMenuDataEntityAri: buildNavigationAriForMenuData({
				activationId: activationId || '',
				firstPartyApp,
				siteId: siteId || '',
			}),
			navPlatSidebarDataEntityAri: buildNavigationAriForSidebarData({
				firstPartyApp,
				siteId: siteId || '',
				activationId: activationId || '',
			}),
			ownerAri,
		}),
		[activationId, firstPartyApp, entityAri, ownerAri, siteId],
	);

	const gsnData = useLazyLoadQuery<GlobalSideNavigationAGGRelayConnectionQuery>(
		GlobalSideNavigationAGGRelayConnectionQueryNode,
		queryVariables,
		{ fetchPolicy: 'store-or-network' },
	);

	return <>{children({ queryRef: gsnData })}</>;
}
